import { DiscussionSummary } from "@cnx/common";
import { Box, Text } from "@mantine/core";
import DiscussionIcon from "components/Chat/ui/DiscussionIcon";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { getContextDiscussionLabel, getTimeAgoLabel } from "./chatRooms.utils";
import { LastMessagePreview } from "./LastMessagePreview";

interface ContextDiscussionListItemProps {
  discussion: DiscussionSummary;
  currentUserId: string | null | undefined;
  typingMessage?: string;
  onSelect: (discussionId: string, contextId?: string | null) => void;
}

export const ContextDiscussionListItem: FC<ContextDiscussionListItemProps> = ({
  discussion,
  currentUserId,
  typingMessage,
  onSelect,
}) => {
  const { t } = useTranslation("discussions");

  const timeLabel = discussion.lastMessageCreatedTime
    ? getTimeAgoLabel(new Date(discussion.lastMessageCreatedTime).getTime(), t)
    : null;

  const isUnread =
    discussion.lastMessageCreatedBy !== currentUserId &&
    (discussion.unreadMessageCount ?? 0) > 0;

  return (
    <Box
      component="button"
      className="w-full px-3 border-t border-gray bg-white text-left cursor-pointer"
      style={{ paddingTop: 10, paddingBottom: 10 }}
      onClick={() => onSelect(discussion.id, discussion.contextId)}
    >
      <Box className="flex flex-col gap-0.5">
        <Box className="flex flex-row items-center justify-between">
          <DiscussionIcon
            discussionType={discussion.discussionType}
            context={discussion.context}
            stopActivityKind={discussion.stopActivityKind}
            size={34}
          />
          <Box className="flex items-center gap-1.5">
            <Text
              fz="sm"
              c={isUnread ? "var(--color-blue)" : "var(--color-gray-dark)"}
            >
              {timeLabel}
            </Text>
            {isUnread && (
              <Box
                bg="var(--color-blue)"
                className="text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center shrink-0"
                fz={"sm"}
              >
                {discussion.unreadMessageCount ?? "0"}
              </Box>
            )}
          </Box>
        </Box>
        <Text fz="sm" fw={600}>
          {getContextDiscussionLabel(discussion, t)}
        </Text>
        <Box className="flex flex-row items-center justify-between gap-2">
          <Box className="flex-1 min-w-0">
            <LastMessagePreview
              discussion={discussion}
              currentUserId={currentUserId}
              typingMessage={typingMessage}
              showSender
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
